import { listLineHighlightRuns } from "./textHighlights";
import { isVerticalTextOrientation } from "./textOrientation";

export const INLINE_IMAGE_KIND = "image";
export const INLINE_IMAGE_CAP_EM = 0.72;

export const isInlineImageRun = (run) => run?.kind === INLINE_IMAGE_KIND;

const isWordChar = (ch) => Boolean(ch) && /[\p{L}\p{N}_]/u.test(ch);

export const createInlineReplacementId = () =>
  `ir_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const normalizeInlineReplacement = (slot) => {
  if (!slot || typeof slot !== "object") return null;
  const id = String(slot.id || "").trim();
  if (!id) return null;
  const phrase = String(slot.phrase || "").trim();
  const assetRef = String(slot.assetRef || "").trim();
  const next = { id };
  if (phrase) next.phrase = phrase;
  if (assetRef) next.assetRef = assetRef;
  return next;
};

export const listStoredInlineReplacements = (replacements = []) =>
  (Array.isArray(replacements) ? replacements : [])
    .map(normalizeInlineReplacement)
    .filter(Boolean);

export const areInlineReplacementListsEqual = (left, right) => {
  const a = listStoredInlineReplacements(left);
  const b = listStoredInlineReplacements(right);
  if (a.length !== b.length) return false;
  return a.every((slot, index) => {
    const other = b[index];
    return (
      slot.id === other.id &&
      (slot.phrase || "") === (other.phrase || "") &&
      (slot.assetRef || "") === (other.assetRef || "")
    );
  });
};

/** Image-edit layers store the uploaded URL on assetRef (no assets catalog). */
export const urlsFromInlineReplacementAssetRefs = (replacements = []) => {
  const urls = {};
  listStoredInlineReplacements(replacements).forEach((slot) => {
    if (slot.assetRef && !urls[slot.assetRef]) {
      urls[slot.assetRef] = slot.assetRef;
    }
  });
  return urls;
};

export const replacementAspect = (metrics, assetRef) => {
  const aspect = Number(metrics?.[assetRef]?.aspect);
  return Number.isFinite(aspect) && aspect > 0 ? aspect : 0;
};

export const listActiveInlineReplacements = (
  replacements = [],
  metrics = {}
) =>
  listStoredInlineReplacements(replacements)
    .map((slot) => {
      if (!slot.phrase || !slot.assetRef) return null;
      const aspect = replacementAspect(metrics, slot.assetRef);
      if (!aspect) return null;
      return { ...slot, aspect };
    })
    .filter(Boolean);

export const resolveInlineReplacementUrls = ({
  replacements = [],
  assets = {},
} = {}) => {
  const urls = {};
  listStoredInlineReplacements(replacements).forEach((slot) => {
    if (!slot.assetRef || urls[slot.assetRef]) return;
    const url = assets?.[slot.assetRef]?.url;
    if (typeof url === "string" && url.trim()) {
      urls[slot.assetRef] = url.trim();
    }
  });
  return urls;
};

export const collectInlineReplacementAssetRefs = (nodes = {}) => {
  const refs = [];
  const seen = new Set();
  const walk = (map) => {
    Object.values(map || {}).forEach((node) => {
      if (node?.type === "text") {
        listStoredInlineReplacements(node.style?.inlineReplacements).forEach(
          (slot) => {
            if (slot.assetRef && !seen.has(slot.assetRef)) {
              seen.add(slot.assetRef);
              refs.push(slot.assetRef);
            }
          }
        );
      }
      if (node?.children) walk(node.children);
    });
  };
  walk(nodes);
  return refs;
};

export const capHeightFromMetrics = (metrics, fontSize) => {
  const size = Number(fontSize);
  const fallback =
    Number.isFinite(size) && size > 0 ? size * INLINE_IMAGE_CAP_EM : 0;
  const ascent = metrics?.actualBoundingBoxAscent;
  if (Number.isFinite(ascent) && ascent > 0) return ascent;
  return fallback;
};

export const imageRunSize = ({ capHeight, aspect } = {}) => {
  const height = Number(capHeight);
  const ratio = Number(aspect);
  if (!(Number.isFinite(height) && height > 0)) {
    return { width: 0, height: 0 };
  }
  if (!(Number.isFinite(ratio) && ratio > 0)) {
    return { width: 0, height };
  }
  return { width: height * ratio, height };
};

export const findExactPhraseOccurrences = ({ text, phrase } = {}) => {
  const source = String(text || "");
  const needle = String(phrase || "");
  if (!needle) return [];
  const ranges = [];
  let from = 0;
  while (from <= source.length - needle.length) {
    const index = source.indexOf(needle, from);
    if (index < 0) break;
    const before = index === 0 ? "" : source[index - 1];
    const afterIndex = index + needle.length;
    const after = afterIndex >= source.length ? "" : source[afterIndex];
    if (!isWordChar(before) && !isWordChar(after)) {
      ranges.push({ start: index, end: afterIndex });
    }
    from = index + needle.length;
  }
  return ranges;
};

export const collectExactReplacementRanges = ({
  text,
  replacements = [],
  metrics = {},
} = {}) => {
  const source = String(text || "");
  const slots = listActiveInlineReplacements(replacements, metrics);
  if (!source || !slots.length) return [];
  const all = [];
  slots.forEach((slot) => {
    findExactPhraseOccurrences({ text: source, phrase: slot.phrase }).forEach(
      (range) => {
        all.push({
          ...range,
          phrase: slot.phrase,
          assetRef: slot.assetRef,
          aspect: slot.aspect,
        });
      }
    );
  });
  all.sort((left, right) => {
    if (left.start !== right.start) return left.start - right.start;
    return right.end - right.start - (left.end - left.start);
  });
  const picked = [];
  all.forEach((range) => {
    const overlaps = picked.some(
      (item) => range.start < item.end && range.end > item.start
    );
    if (!overlaps) picked.push(range);
  });
  return picked;
};

export const wrapUnitsFromParagraph = ({
  paragraph,
  paragraphStart = 0,
  replacements = [],
  metrics = {},
} = {}) => {
  const source = String(paragraph || "");
  const ranges = collectExactReplacementRanges({
    text: source,
    replacements,
    metrics,
  });
  const units = [];
  const pushWords = (chunk, chunkStart) => {
    const matcher = /\S+/g;
    let match = matcher.exec(chunk);
    while (match) {
      units.push({
        kind: "text",
        text: match[0],
        start: chunkStart + match.index,
        end: chunkStart + match.index + match[0].length,
      });
      match = matcher.exec(chunk);
    }
  };
  let cursor = 0;
  ranges.forEach((range) => {
    pushWords(source.slice(cursor, range.start), paragraphStart + cursor);
    units.push({
      kind: INLINE_IMAGE_KIND,
      text: range.phrase,
      phrase: range.phrase,
      assetRef: range.assetRef,
      aspect: range.aspect,
      start: paragraphStart + range.start,
      end: paragraphStart + range.end,
    });
    cursor = range.end;
  });
  pushWords(source.slice(cursor), paragraphStart + cursor);
  return units;
};

const imageRunFromRange = (range) => ({
  kind: INLINE_IMAGE_KIND,
  text: range.phrase,
  phrase: range.phrase,
  assetRef: range.assetRef,
  aspect: range.aspect,
  style: null,
});

const splitRunsWithImages = (line, runs, ranges) => {
  const source = String(line || "");
  if (!ranges.length) {
    return Array.isArray(runs) && runs.length
      ? runs
      : [{ text: source, style: null }];
  }
  const out = [];
  const emitted = new Set();
  let offset = 0;
  const consume = (from, to, style) => {
    let cursor = from;
    ranges.forEach((range) => {
      if (range.end <= from || range.start >= to) return;
      const clipStart = Math.max(range.start, from);
      if (clipStart > cursor) {
        out.push({ text: source.slice(cursor, clipStart), style });
      }
      if (!emitted.has(range.start)) {
        out.push(imageRunFromRange(range));
        emitted.add(range.start);
      }
      cursor = Math.min(range.end, to);
    });
    if (cursor < to) {
      out.push({ text: source.slice(cursor, to), style });
    }
  };
  const list =
    Array.isArray(runs) && runs.length
      ? runs
      : [{ text: source, style: null }];
  list.forEach((run) => {
    const runText = String(run?.text || "");
    consume(offset, offset + runText.length, run.style);
    offset += runText.length;
  });
  return out.filter((run) => isInlineImageRun(run) || Boolean(run.text));
};

export const listLineInlineRuns = ({
  text,
  lines = [],
  highlights,
  phrases,
  replacements,
  metrics,
  orientation,
  selectionPhrase = "",
} = {}) => {
  const highlightRuns = listLineHighlightRuns({
    text,
    lines,
    highlights,
    phrases,
    orientation,
    selectionPhrase,
  });
  if (isVerticalTextOrientation(orientation)) return highlightRuns;
  const active = listActiveInlineReplacements(replacements, metrics);
  if (!active.length) return highlightRuns;
  return (Array.isArray(lines) ? lines : []).map((line, index) => {
    const source = String(line || "");
    const ranges = collectExactReplacementRanges({
      text: source,
      replacements: active,
      metrics,
    });
    const baseRuns = highlightRuns?.[index] || [
      { text: source, style: null },
    ];
    if (!ranges.length) return baseRuns;
    return splitRunsWithImages(source, baseRuns, ranges);
  });
};
